var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, } from "react";
import { UpdateStream } from "../services";
import { sendLocal } from "@sinch/bus";
/**
 * SocketConnector
 *
 * You may want to pass an optional ref prop to access the socket client instance
 */
export const SocketConnector = forwardRef(({ socketUrl, token, projectId, apiUrl, onMessage, onConnect, onDisconnect, onTooManyRetries, onAuthorize, onError, disableBus, debug, autoReconnect, }, forwardedRef) => {
    // Create reference for the socket client
    const socketRef = useRef();
    // Provide socket reference to the parent component
    useImperativeHandle(forwardedRef, () => socketRef.current);
    const handleMessage = useCallback((message) => {
        const { payload } = message, meta = __rest(message, ["payload"]);
        // custom message handling if defined
        if (onMessage) {
            // eslint-disable-next-line no-console
            debug &&
                console.debug("SocketConnector: Custom message handle.", message);
            onMessage(message);
            if (disableBus) {
                return;
            }
        }
        const busMessage = {
            type: "UPDATE_STREAM",
            payload: payload,
            meta: meta,
        };
        // eslint-disable-next-line no-console
        debug &&
            console.debug("SocketConnector: Send bus message.", busMessage);
        sendLocal(busMessage);
    }, [debug, disableBus, onMessage]);
    const handleAuthorize = useCallback((deviceId) => {
        // eslint-disable-next-line no-console
        debug && console.debug("SocketConnector: Authorized.");
        onAuthorize && onAuthorize(deviceId);
    }, [debug, onAuthorize]);
    const handleConnect = useCallback((deviceId) => {
        // eslint-disable-next-line no-console
        debug && console.debug("SocketConnector: Connected.");
        onConnect && onConnect(deviceId);
    }, [debug, onConnect]);
    const handleDisconnect = useCallback(() => {
        // eslint-disable-next-line no-console
        debug && console.debug("SocketConnector: Disconnected.");
        onDisconnect && onDisconnect();
    }, [debug, onDisconnect]);
    const handleError = useCallback((error) => {
        debug && console.error("SocketConnector: An error occurred.", error);
        onError && onError(error);
    }, [debug]);
    const handleTooManyRetries = useCallback(() => {
        debug && console.error("SocketConnector: Too many retries.");
        onTooManyRetries && onTooManyRetries();
    }, [debug]);
    useEffect(() => {
        var _a;
        // Wait for parameters to be set until initialization
        if (!projectId || !token || !apiUrl || !handleMessage) {
            // eslint-disable-next-line no-console
            debug && console.debug("SocketConnector: Missing parameters.");
            return;
        }
        // Create config for UpdateStream
        const config = {
            // required:
            token: token,
            projectId: projectId,
            apiUrl: apiUrl,
            onMessage: handleMessage,
            // optional:
            onConnect: handleConnect,
            onAuthorize: handleAuthorize,
            onDisconnect: handleDisconnect,
            onTooManyRetries: handleTooManyRetries,
            onError: handleError,
            autoReconnect: autoReconnect,
            debug: debug,
        };
        // Create only if not already initialized
        if (!socketRef.current) {
            // eslint-disable-next-line no-console
            debug &&
                console.debug("SocketConnector: All parameters set. Initializing...");
            socketRef.current = new UpdateStream(socketUrl, config);
        }
        else {
            debug &&
                console.debug("SocketConnector: Parameters changed. Update config.");
            // make sure socket always has the latest config in case it has to re-authorize etc.
            (_a = socketRef === null || socketRef === void 0 ? void 0 : socketRef.current) === null || _a === void 0 ? void 0 : _a.setConfig(config);
        }
    }, [
        token,
        socketUrl,
        projectId,
        apiUrl,
        handleMessage,
        handleConnect,
        handleAuthorize,
        handleDisconnect,
        handleError,
        autoReconnect,
        debug,
    ]);
    // reconnect on project id change (should not happen since the page is reloading on channel switch but anyways...)
    useEffect(() => () => {
        var _a, _b;
        (_a = socketRef.current) === null || _a === void 0 ? void 0 : _a.disconnect();
        (_b = socketRef.current) === null || _b === void 0 ? void 0 : _b.connect();
    }, [projectId]);
    // disconnect on unmount
    useEffect(() => () => {
        var _a;
        (_a = socketRef.current) === null || _a === void 0 ? void 0 : _a.disconnect();
    }, []);
    return null;
});
